import { Paper, Stack, Typography } from "@mui/material";
import React from "react";
import { IComponentParent } from "sonobello.utilities.react";

interface IBaseAuthenticationCardProps extends IComponentParent {
  /** The content t hat should be displayed at the top of the card. */
  header: React.ReactNode;
  /** The content that should be displayed at the bottom of the card. */
  footer: React.ReactNode;
  /** The salutation to the user displayed above the login form. */
  salutation: string;
}

const BaseAuthenticationCard: React.FC<IBaseAuthenticationCardProps> = ({ header, footer, salutation, children }) => {
  return (
    <Paper sx={{ p: 2, minWidth: "30rem" }}>
      <Stack alignItems="center" spacing={2}>
        {header}
        <Typography variant="body1">{salutation}</Typography>
        {children}
        {footer}
      </Stack>
    </Paper>
  );
};

export interface IAuthenticationCardProps extends Omit<IBaseAuthenticationCardProps, "header"> {
  /** The raw text that should be shown in the header. */
  headerText: string | null;
  /** The simple icon without text that should be shown in the header. */
  headerIcon: React.ReactNode;
}

/** A surface component which organizes a header title, header icon, directive, and custom content. */
export const AuthenticationCard: React.FC<IAuthenticationCardProps> = ({
  headerIcon,
  headerText,
  children,
  ...rest
}) => {
  return (
    <BaseAuthenticationCard
      {...rest}
      header={
        <Stack direction="row" spacing={2} alignItems="center">
          {headerIcon}
          <Typography variant="h3">{headerText}</Typography>
        </Stack>
      }
    >
      {children}
    </BaseAuthenticationCard>
  );
};

export interface IAuthenticationLogoCardProps extends Omit<IBaseAuthenticationCardProps, "header"> {
  /** The full content that should be used as the header. */
  logo: React.ReactNode;
}

/** A surface component which organizes a logo, directive, and custom content. */
export const AuthenticationLogoCard: React.FC<IAuthenticationLogoCardProps> = ({ logo, children, ...rest }) => {
  return (
    <BaseAuthenticationCard {...rest} header={logo}>
      {children}
    </BaseAuthenticationCard>
  );
};
